'use client';

import { useSyncExternalStore } from 'react';

/**
 * Tiny external store, not React Context: `Header` (site-wide, rendered in the root layout) and
 * `PreviewClient` (a page) are siblings under `<main>`, not parent/child — Context would need a
 * Provider wired into the root layout just for this one narrow feature. A page that wants to
 * guard navigation while it's doing something the user shouldn't casually walk away from calls
 * `setNavigationGuard(true/false, message)`; `Header` subscribes via `useNavigationGuard()` and
 * intercepts its own links whenever a guard is active, from any route, with no wiring per-page.
 */

export interface NavigationGuardState {
  active: boolean;
  message: string;
}

const NONE: NavigationGuardState = { active: false, message: '' };
let state: NavigationGuardState = NONE;
const listeners = new Set<() => void>();

export function setNavigationGuard(active: boolean, message = ''): void {
  const next = active ? { active, message } : NONE;
  if (state.active === next.active && state.message === next.message) return;
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): NavigationGuardState {
  return state;
}

function getServerSnapshot(): NavigationGuardState {
  return NONE;
}

export function useNavigationGuard(): NavigationGuardState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
